import React, { Component } from "react";
import { connect } from "react-redux";
import {
	Button,
	CheckBox,
	OverlayPage,
	OverlayPageBottomButtons,
	OverlayPageContents,
	OverlayPageSection,
	Spinner,
	SuccessPopup,
} from "../../components";
import { Text } from "../../components/text";
import { profileActions } from "../../webapi";
import { validateAccess, isFeatureEnabled } from "../../session";
import { getSiteNameFromRoles } from "../../helper";
import { extensionNotificationPreferences } from "../../config/features";

/**
 * Core feature toggles (not provided by extensions).
 * Events is a core feature, not an extension, so it's defined here.
 */
const CORE_NOTIFICATION_TOGGLES = [
	{
		key: "eventComments",
		label: "Event comments",
		feature: "events",
		permission: "events",
	},
];

/**
 * Combined notification toggles: core features + extension-injected preferences.
 * Extensions inject their preferences via feature.config.js → notificationPreference.
 * Sorted alphabetically by label for consistent display order.
 */
const NOTIFICATION_TOGGLES = [
	...CORE_NOTIFICATION_TOGGLES,
	...extensionNotificationPreferences,
].sort((a, b) => a.label.localeCompare(b.label));

class PersonalSettings extends Component {
	state = {
		loading: true,
		submitting: false,
		success: false,
		prefs: {},
	};

	componentDidMount() {
		this.loadSettings();
	}

	getVisibleToggles = () => {
		const { auth } = this.props;
		return NOTIFICATION_TOGGLES.filter(
			(toggle) =>
				isFeatureEnabled(auth.features, toggle.feature) &&
				validateAccess(auth.site, toggle.permission, auth),
		);
	};

	loadSettings = async () => {
		try {
			const { data } = await profileActions.getPersonalSettings(
				this.props.auth.site,
			);
			const notificationPreferences =
				(data && data.NotificationPreferences) || {};
			const prefs = {};
			NOTIFICATION_TOGGLES.forEach((toggle) => {
				prefs[toggle.key] = !!notificationPreferences[toggle.key];
			});
			this.setState({ prefs, loading: false });
		} catch (error) {
			console.log("Failed to load personal settings:", error);
			this.setState({ loading: false });
		}
	};

	onToggle = (key) => {
		this.setState((prevState) => ({
			prefs: {
				...prevState.prefs,
				[key]: !prevState.prefs[key],
			},
			success: false,
		}));
	};

	handleSave = async () => {
		this.setState({ submitting: true, success: false });
		try {
			const visibleKeys = this.getVisibleToggles().map((t) => t.key);
			const payload = {};
			visibleKeys.forEach((key) => {
				payload[key] = !!this.state.prefs[key];
			});
			await profileActions.setPersonalSettings(this.props.auth.site, payload);
			this.setState({ submitting: false, success: true });
		} catch (error) {
			console.log("Failed to save personal settings:", error);
			this.setState({ submitting: false });
			alert("Something went wrong. Please try again.");
		}
	};

	renderContent() {
		if (this.state.loading) {
			return <Spinner />;
		}

		const visibleToggles = this.getVisibleToggles();

		return (
			<div>
				<Text type="formTitleLarge" className="marginBottom-16">
					Personal Settings
				</Text>
				<Text type="body" className="marginBottom-24">
					These settings apply to{" "}
					<span className="font-semibold">
						{getSiteNameFromRoles(
							this.props.auth.site,
							this.props.auth.user.Roles,
						)}
					</span>{" "}
					only.
				</Text>
				<Text type="formTitleSmall" className="marginBottom-8">
					Push Notification Preferences
				</Text>
				<Text type="help" className="marginBottom-16">
					Receive notifications for all comments on these features, even if you
					haven't commented.
				</Text>
				{visibleToggles.length === 0 && (
					<Text type="body">
						No notification preferences available for your permissions.
					</Text>
				)}
				{visibleToggles.map((toggle) => (
					<CheckBox
						key={toggle.key}
						label={toggle.label}
						className="marginTop-10"
						isActive={!!this.state.prefs[toggle.key]}
						onChange={() => this.onToggle(toggle.key)}
					/>
				))}
			</div>
		);
	}

	renderSuccess() {
		if (!this.state.success) {
			return null;
		}
		return (
			<SuccessPopup
				text="Settings have been saved"
				buttons={[
					{
						type: "outlined",
						onClick: () => {
							window.history.back();
						},
						text: "Go back",
					},
				]}
			/>
		);
	}

	render() {
		return (
			<OverlayPage>
				<OverlayPageContents noBottomButtons={this.state.success}>
					<OverlayPageSection className="pageSectionWrapper--newPopup">
						<div className="padding-60 paddingVertical-40">
							{this.renderContent()}
						</div>
						{this.renderSuccess()}
					</OverlayPageSection>
				</OverlayPageContents>
				<OverlayPageBottomButtons>
					<div className="flex flex-center">
						<Button
							inline
							buttonType="tertiary"
							onClick={() => {
								window.history.back();
							}}
							isActive
							style={{ marginRight: 16 }}
						>
							Back
						</Button>
						<Button
							inline
							buttonType="primary"
							onClick={this.handleSave}
							isActive={!this.state.submitting && !this.state.loading}
						>
							{this.state.submitting ? "Saving..." : "Save"}
						</Button>
					</div>
				</OverlayPageBottomButtons>
			</OverlayPage>
		);
	}
}

const mapStateToProps = (state) => {
	const { auth } = state;
	return { auth };
};

export default connect(mapStateToProps)(PersonalSettings);
